import { useMemo } from 'react';
import { format, subMonths, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { Expense } from '@/hooks/finance/useExpenses';

interface ExpensesDashboardProps {
  expenses: Expense[];
  getCategoryName: (id: string | null) => string;
}

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--destructive))',
  'hsl(210, 70%, 50%)',
  'hsl(150, 60%, 45%)',
  'hsl(45, 80%, 55%)',
  'hsl(280, 60%, 55%)',
  'hsl(330, 60%, 55%)',
  'hsl(180, 50%, 45%)',
];

export function ExpensesDashboard({ expenses, getCategoryName }: ExpensesDashboardProps) {
  const categoryData = useMemo(() => {
    const map = new Map<string, number>();
    expenses.forEach((e) => {
      if (e.status === 'cancelled') return;
      const name = getCategoryName(e.category_id);
      map.set(name, (map.get(name) || 0) + e.amount);
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [expenses, getCategoryName]);

  const monthlyData = useMemo(() => {
    const months: { month: string; key: string; total: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = subMonths(new Date(), i);
      const key = format(startOfMonth(d), 'yyyy-MM');
      months.push({
        month: format(d, 'MMM/yy', { locale: ptBR }),
        key,
        total: 0,
      });
    }
    expenses.forEach((e) => {
      if (e.status === 'cancelled') return;
      const key = e.due_date.substring(0, 7);
      const found = months.find((m) => m.key === key);
      if (found) found.total += e.amount;
    });
    return months.map((m) => ({ month: m.month, total: Math.round(m.total * 100) / 100 }));
  }, [expenses]);

  if (expenses.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Despesas por Categoria
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} (${(percent * 100).toFixed(0)}%)`
                  }
                  labelLine={false}
                >
                  {categoryData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) =>
                    value.toLocaleString('pt-BR', { style: 'currency', currency: 'USD' })
                  }
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Evolução Mensal (últimos 6 meses)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="month" className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip
                  formatter={(value: number) =>
                    value.toLocaleString('pt-BR', { style: 'currency', currency: 'USD' })
                  }
                />
                <Bar dataKey="total" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
